import { Box } from '@chakra-ui/react';
import { Card, Container, ExternalLayout, Group, IconButton } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';
import { usePageView } from '@melio/vex-domain';
import { FooterWidget, PoweredByMelioWidget, VerificationCode } from '@melio/vex-widgets';
import { useEffect } from 'react';

export type CodeVerificationScreenProps = {
  onBack: VoidFunction;
  onDone: (code: string, email: string) => Promise<boolean | void>;
  generateNewCode: (emailAddress: string) => void;
  email: string | null;
  showPoweredByMelio?: boolean;
  helpLinkHref: string;
  resendEmailError?: string;
};

export const CodeVerificationScreen = forwardRef<CodeVerificationScreenProps, 'div'>(
  (
    { onBack, onDone, generateNewCode, email, showPoweredByMelio, helpLinkHref, resendEmailError, children, ...props },
    ref
  ) => {
    usePageView('Vendor', {
      PageName: 'enter-code',
      Intent: 'complete-verification',
      Email: email,
    });

    const handleDone = (...args: Parameters<CodeVerificationScreenProps['onDone']>) => onDone(...args);

    useEffect(() => {
      if (!email) {
        onBack();
      }
    }, [email, onBack]);

    if (!email) {
      return null;
    }

    const content = (
      <Card paddingX="none" paddingY="none">
        <Group variant="vertical" spacing="none" width="full">
          <Container paddingX="xl" paddingY="l" backgroundColor="light">
            <Box gap="l">
              <IconButton aria-label="back" variant="naked" onClick={onBack} icon="chevron-left" size="large" />
            </Box>
          </Container>
          <Container paddingX="xl" paddingY="l">
            <Group variant="vertical">
              <VerificationCode
                emailAddress={email}
                generateNewCode={generateNewCode}
                helpLinkHref={helpLinkHref}
                onCodeComplete={handleDone}
                resendEmailError={resendEmailError}
              />
            </Group>
          </Container>
        </Group>
      </Card>
    );

    const footer = <FooterWidget>{showPoweredByMelio && <PoweredByMelioWidget />}</FooterWidget>;

    return (
      <ExternalLayout
        data-component="CodeVerificationScreen"
        data-testid="code-verification-screen"
        {...props}
        content={content}
        footer={footer}
        ref={ref}
      />
    );
  }
);

CodeVerificationScreen.displayName = 'CodeVerificationScreen';
